import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import {
  IonButton,
  IonInput,
  IonRow,
  IonCol,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonLoading,
} from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import PayActions from 'redux/PayRedux';
import { AuthSelectors } from 'redux/AuthRedux';
import get from 'lodash/get';
import api_v2 from 'services/api_v2';
import validationSchema from './schema';

function AddCardForm() {
  const authToken = useSelector(AuthSelectors.selectToken);
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const formik = useFormik({
    initialValues: {
      name: '',
      month: '',
      year: '',
      zip: '',
    },
    onSubmit: (values) => {
      window.Spreedly.tokenizeCreditCard({
        full_name: values.name,
        year: values.year,
        month: values.month,
        zip: values.zip,
        retain_on_success: true,
      });
    },
    validationSchema,
  });

  useEffect(() => {
    async function saveCard(token) {
      try {
        const api = api_v2(authToken);

        const resultSaveCard = await api.saveCard(token);
        formik.setSubmitting(false);
        dispatch(PayActions.addPaymentMethod(resultSaveCard));

        if (get(location, 'state.notice')) {
          history.replace('/locations');
        } else {
          history.goBack();
        }
      } catch (error) {
        formik.setSubmitting(false);
        formik.setFieldError('card', 'There was problem saving card');
      }
    }

    window.Spreedly.init(process.env.REACT_APP_SPREEDLY_ENV_KEY, {
      numberEl: 'spreedly-number',
      cvvEl: 'spreedly-cvv',
    });

    window.Spreedly.on('ready', () => {
      window.Spreedly.setFieldType('number', 'text');
      window.Spreedly.setPlaceholder('number', 'XXXX XXXX XXXX XXXX');
      window.Spreedly.setPlaceholder('cvv', 'CVV');
      window.Spreedly.setNumberFormat('prettyFormat');
      window.Spreedly.setStyle(
        'number',
        'height: 18px;padding: 10px 0; color: #333; background: #eee; font-size: 16px; width: 100%;'
      );
      window.Spreedly.setStyle(
        'cvv',
        'height: 18px;padding: 10px 0; color: #333; background: #eee; font-size: 16px; width: 100%;'
      );
    });

    if (window.spreedlyInitialized) return;

    window.Spreedly.on('errors', (errors) => {
      if (errors && errors[0]) {
        formik.setFieldError('card', errors[0].message);
      }
      formik.setSubmitting(false);
    });

    window.Spreedly.on('paymentMethod', async (token) => {
      saveCard(token);
    });

    window.spreedlyInitialized = true;

    return () => {
      window.spreedlyInitialized = false;
      window.Spreedly.removeHandlers();
    };
  }, []);

  return (
    <form onSubmit={formik.handleSubmit}>
      <IonLoading
        isOpen={formik.isSubmitting}
        message="Saving payment method..."
      />
      <IonList>
        <IonItem lines="full">
          <IonLabel position="stacked">Name</IonLabel>
          <IonInput
            name="name"
            placeholder="Name on Card"
            disabled={formik.isSubmitting}
            onIonChange={formik.handleChange}
            value={formik.values.name}
            autoCapitalize="words"
          />
          {formik.submitCount > 0 && (
            <IonText color="danger">
              <small>{formik.errors.name}</small>
            </IonText>
          )}
        </IonItem>
        <IonItem lines="full">
          <IonLabel position="stacked">Credit Card Number / CVV</IonLabel>
          <IonRow style={{ marginTop: 5, marginBottom: -5 }}>
            <IonCol sizeXs={9}>
              <div id="spreedly-number" className="spreedly" />
            </IonCol>
            <IonCol sizeXs={3}>
              <div id="spreedly-cvv" className="spreedly" />
            </IonCol>
          </IonRow>
          {formik.submitCount > 0 && (
            <IonText color="danger">
              <small>{formik.errors.card}</small>
            </IonText>
          )}
        </IonItem>
        <IonItem lines="full">
          <IonRow>
            <IonCol sizeXs={8}>
              <IonLabel position="stacked">Expiration Date</IonLabel>
              <IonRow>
                <IonCol sizeXs={6}>
                  <IonInput
                    name="month"
                    type="tel"
                    disabled={formik.isSubmitting}
                    onIonChange={formik.handleChange}
                    value={formik.values.month}
                    placeholder="MM"
                    maxlength={2}
                  />
                </IonCol>
                <IonCol sizeXs={6}>
                  <IonInput
                    name="year"
                    placeholder="YYYY"
                    type="tel"
                    disabled={formik.isSubmitting}
                    onIonChange={formik.handleChange}
                    value={formik.values.year}
                    maxlength={4}
                  />
                </IonCol>
              </IonRow>
              {formik.submitCount > 0 && (
                <IonText color="danger">
                  <small>{formik.errors.year || formik.errors.month}</small>
                </IonText>
              )}
            </IonCol>
            <IonCol sizeXs={4}>
              <IonLabel position="stacked">Zip Code</IonLabel>
              <IonInput
                name="zip"
                placeholder="Zip"
                type="tel"
                disabled={formik.isSubmitting}
                onIonChange={formik.handleChange}
                value={formik.values.zip}
              />
              {formik.submitCount > 0 && (
                <IonText color="danger">
                  <small>{formik.errors.zip}</small>
                </IonText>
              )}
            </IonCol>
          </IonRow>
        </IonItem>

        <div className="ion-v-padding">
          <IonButton
            color="secondary"
            expand="block"
            fill="solid"
            type="submit"
            disabled={formik.isSubmitting}
          >
            Save Card
          </IonButton>
        </div>
      </IonList>
    </form>
  );
}

export default AddCardForm;
